import { useMemo, useCallback, useState } from "react";
import {
  ExternalLink,
  Layers,
  Calendar,
  User,
  X,
  Download,
  FileText,
  Globe,
  Pencil,
  Trash2,
  Check,
} from "lucide-react";
import { useConfirm } from "@omit/react-confirm-dialog";
import { cn } from "@/modern/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { getMarketingDocumentDownloadLink } from "@/network/network";
import { showToast } from "@/other/utils/utils";
import type { MarketingPage } from "@/bryntum-lib/models/MarketingPage";
import type { MarketingDocument } from "@/bryntum-lib/models/MarketingDocument";
import type { MarketingWorkFile } from "@/bryntum-lib/models/MarketingWorkFile";
import type { AsyncTask } from "@/modern/components/async-tasks/types";
import { AsyncTaskUI, useAsyncTaskUIContext } from "@/modern/components/async-tasks/async-task-ui";
import {
  matchBoardMarketingDocumentGenerationTask,
  matchBoardMarketingPageGenerationTask,
} from "@/modern/components/async-tasks/utils";
import { MarketingPageGeneratingPanelRow } from "@/react/components/entityViews/marketingPages/components/MarketingPageGeneratingSkeleton";
import {
  isSandboxBoardRecord,
  TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG,
} from "@/react/components/entityViews/marketingPages/utils";
import { MarketingDocumentGeneratingPanelRow } from "./MarketingDocumentGeneratingSkeleton";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button-shadcn";

function formatRelative(d: Date | string | null | undefined): string {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function buildCreatorNameMap(
  records: { id: number; creator: number | null }[],
  project: { boardUsersStore?: { getById: (id: number) => { name?: string } | undefined } } | null | undefined,
): Map<number, string | null> {
  const map = new Map<number, string | null>();
  for (const record of records) {
    if (record.creator == null) {
      map.set(record.id, null);
      continue;
    }
    const bu = project?.boardUsersStore?.getById(record.creator);
    map.set(record.id, bu?.name ?? null);
  }
  return map;
}

interface LinkedPageRowProps {  page: MarketingPage;
  creatorName: string | null;
  onNavigate: (id: number) => void;
}

const LinkedPageRow = ({ page, creatorName, onNavigate }: LinkedPageRowProps) => {
  const externalHref = page.url
    ? page.url.startsWith("http") ? page.url : `https://${page.url}`
    : null;

  return (
    <div className="group relative rounded-lg border border-border/60 bg-card transition-all hover:border-primary/30 hover:shadow-sm">
      <button
        type="button"
        onClick={() => onNavigate(Number(page.id))}
        className="flex w-full flex-col gap-2 p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-lg"
      >
        <div className="flex items-start gap-2 pr-7">
          <span className="text-[13px] font-medium text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors">
            {page.title || "Untitled page"}
          </span>
        </div>

        {page.slug && (
          <span className="font-mono text-[10px] text-muted-foreground/50 truncate leading-none bg-muted/50 rounded px-1.5 py-1 self-start max-w-full block">
            /{page.slug}
          </span>
        )}

        <div className="flex items-center gap-3 text-[10px] text-muted-foreground/50 pt-0.5">
          {creatorName && (
            <span className="inline-flex items-center gap-1 truncate">
              <User className="size-2.5 shrink-0" />
              <span className="truncate">{creatorName}</span>
            </span>
          )}
          <span className="inline-flex items-center gap-1 ml-auto shrink-0">
            <Calendar className="size-2.5" />
            {formatRelative(page.updatedAt)}
          </span>
        </div>
      </button>

      {externalHref && (
        <a
          href={externalHref}
          target="_blank"
          rel="noopener noreferrer"
          title="Open page in new tab"
          className="absolute top-2.5 right-2.5 rounded-md border border-border/50 bg-muted/60 p-1 transition-colors hover:border-primary/30 hover:bg-primary/5 focus-visible:outline-none"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="size-2.5 text-muted-foreground/50 group-hover:text-primary/60 transition-colors" />
        </a>
      )}
    </div>
  );
};

interface DocumentRowProps {
  doc: MarketingDocument;
  creatorName: string | null;
}

const DocumentRow = ({ doc, creatorName }: DocumentRowProps) => {
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState(doc.title || "");
  const [downloading, setDownloading] = useState(false);

  const formatLabel = (doc.format || "").toUpperCase() || "FILE";

  const handleSaveTitle = useCallback(() => {
    const next = titleDraft.trim();
    if (!next) {
      showToast({ msg: "Title cannot be empty.", type: "error", duration: 2500 });
      return;
    }
    (doc as { title: string }).title = next;
    setEditing(false);
  }, [doc, titleDraft]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const boardId = Number(doc.board);
      if (!boardId) {
        showToast({ msg: "Missing board for this document.", type: "error", duration: 3000 });
        return;
      }
      const res = await getMarketingDocumentDownloadLink(boardId, Number(doc.id));
      const url = typeof res?.data?.download_url === "string" ? res.data.download_url.trim() : "";
      if (!url) {
        showToast({ msg: "No file available to download.", type: "error", duration: 3000 });
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      showToast({ msg: "Failed to download document.", type: "error", duration: 3000 });
    } finally {
      setDownloading(false);
    }
  }, [doc]);

  const handleDelete = useCallback(async () => {
    const confirmed = await confirm({
      title: "Delete document",
      description: `Delete "${doc.displayTitle}"? The stored file will also be removed.`,
    });
    if (!confirmed) return;
    (doc as any).remove();
  }, [confirm, doc]);

  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-3 transition-all",
        "border-border/60 hover:border-primary/30 hover:shadow-sm",
      )}
    >
      {editing ? (
        <div className="flex items-center gap-1.5 mb-2" onClick={(e) => e.stopPropagation()}>
          <Input
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            className="h-7 text-xs"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveTitle();
              if (e.key === "Escape") {
                setTitleDraft(doc.title || "");
                setEditing(false);
              }
            }}
          />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0"
            onClick={handleSaveTitle}
            aria-label="Save title"
          >
            <Check className="size-3.5" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          className="flex w-full items-start gap-2 mb-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded"
          onClick={handleDownload}
          disabled={downloading}
          title="Download document"
        >
          <span className="text-[13px] font-medium text-foreground leading-snug line-clamp-2 flex-1 min-w-0 group-hover:text-primary">
            {doc.displayTitle}
          </span>
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {formatLabel}
          </span>
        </button>
      )}

      {doc.filename && (
        <p className="mb-2 truncate text-[10px] text-muted-foreground/50 font-mono">
          {doc.filename}
        </p>
      )}

      <div className="flex items-center gap-3 text-[10px] text-muted-foreground/50 mb-2">
        {creatorName && (
          <span className="inline-flex items-center gap-1 truncate">
            <User className="size-2.5 shrink-0" />
            <span className="truncate">{creatorName}</span>
          </span>
        )}
        <span className="inline-flex items-center gap-1 ml-auto shrink-0">
          <Calendar className="size-2.5" />
          {formatRelative(doc.updatedAt)}
        </span>
      </div>

      <div className="flex items-center gap-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 flex-1 gap-1 px-2 text-[11px]"
          disabled={downloading}
          onClick={handleDownload}
        >
          <Download className="size-3" />
          {downloading ? "Opening…" : "Download"}
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          aria-label="Rename"
          onClick={() => {
            setTitleDraft(doc.title || "");
            setEditing(true);
          }}
        >
          <Pencil className="size-3" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-destructive hover:text-destructive"
          aria-label="Delete"
          onClick={handleDelete}
        >
          <Trash2 className="size-3" />
        </Button>
      </div>
    </div>
  );
};

interface SectionHeaderProps {
  icon: React.ReactNode;
  label: string;
  count: number;
}

const SectionHeader = ({ icon, label, count }: SectionHeaderProps) => (
  <div className="flex items-center gap-2 px-3 pt-3 pb-1.5">
    <span className="text-muted-foreground/60">{icon}</span>
    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </span>
    {count > 0 && (
      <span className="ml-auto inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-semibold text-muted-foreground">
        {count}
      </span>
    )}
  </div>
);

interface WorkFileOutputsPanelProps {
  pages: MarketingPage[];
  documents: MarketingDocument[];
  /** Scopes AsyncTaskUI.Loader rows to this work file. */
  workFile?: MarketingWorkFile | null;
  /** Overrides the default work-file name under the Outputs header. */
  headerTitle?: string;
  /** When provided, renders a close button in the header. */
  onClose?: () => void;
}

export const WorkFileOutputsPanel = ({
  pages,
  documents,
  workFile = null,
  headerTitle,
  onClose,
}: WorkFileOutputsPanelProps) => {
  const { project, viewManager, selectedBoard } = useSwivuBryntumContext();
  const { pendingTasks } = useAsyncTaskUIContext();
  const boardRecord = selectedBoard?.record as
    | { id?: number; isSandbox?: boolean }
    | undefined;
  const boardId = boardRecord?.id ?? (workFile != null ? Number(workFile.board) : undefined);
  const isTestDrive = isSandboxBoardRecord(boardRecord);
  const workFileId = workFile != null ? Number(workFile.id) : undefined;

  const matchPageGeneration = useCallback(
    (task: AsyncTask) =>
      boardId != null &&
      matchBoardMarketingPageGenerationTask(task, boardId, workFileId),
    [boardId, workFileId],
  );

  const matchDocumentGeneration = useCallback(
    (task: AsyncTask) =>
      boardId != null &&
      matchBoardMarketingDocumentGenerationTask(task, boardId, workFileId),
    [boardId, workFileId],
  );

  const pendingPageCount = useMemo(
    () => pendingTasks.filter(matchPageGeneration).length,
    [pendingTasks, matchPageGeneration],
  );
  const pendingDocumentCount = useMemo(
    () => pendingTasks.filter(matchDocumentGeneration).length,
    [pendingTasks, matchDocumentGeneration],
  );

  const handleNavigate = useCallback((id: number) => {
    const view = viewManager?.getDefaultViewByType(VIEW_TYPES.marketingPages);
    if (view) {
      viewManager?.selectView(view);
      viewManager?.selectViewAnonymousView(view.id, view.name, view.viewType, { marketingPageId: id });
    }
  }, [viewManager]);

  const pageCreatorNames = useMemo(
    () => buildCreatorNameMap(pages, project as any),
    [pages, project],
  );

  const documentCreatorNames = useMemo(
    () => buildCreatorNameMap(documents, project as any),
    [documents, project],
  );

  const totalCount =
    pages.length + documents.length + pendingPageCount + pendingDocumentCount;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="shrink-0 px-4 py-3 border-b border-border bg-accent">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 shrink-0">
            <Layers className="size-3 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-medium text-muted-foreground/70 uppercase tracking-wider leading-none mb-0.5">
              Outputs
            </p>
            {headerTitle && (
              <p className="text-xs font-medium text-foreground truncate leading-snug">
                {headerTitle}
              </p>
            )}
          </div>
          {totalCount > 0 && (
            <span className="shrink-0 inline-flex items-center justify-center h-5 min-w-5 rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary leading-none">
              {totalCount}
            </span>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close panel"
              className={cn(
                "shrink-0 rounded-md p-1 text-muted-foreground/50 transition-colors",
                "hover:bg-muted hover:text-foreground focus-visible:outline-none",
              )}
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto bg-background">
        <SectionHeader
          icon={<Globe className="size-3" />}
          label="Pages"
          count={pages.length + pendingPageCount}
        />
        {pages.length === 0 && pendingPageCount === 0 ? (
          <div className="px-5 py-4 text-center">
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              {isTestDrive
                ? TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG
                : "Publish this work file to generate a page."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 px-3 pb-2">
            <AsyncTaskUI.Loader forTask={matchPageGeneration}>
              <MarketingPageGeneratingPanelRow />
            </AsyncTaskUI.Loader>
            {pages.map((page) => (
              <LinkedPageRow
                key={page.id}
                page={page}
                creatorName={pageCreatorNames.get(page.id) ?? null}
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        )}

        <div className="mx-3 border-t border-border/60" />

        <SectionHeader
          icon={<FileText className="size-3" />}
          label="Documents"
          count={documents.length + pendingDocumentCount}
        />
        {documents.length === 0 && pendingDocumentCount === 0 ? (
          <div className="px-5 py-4 text-center">
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Generate a DOCX or PDF to download from this work file.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 px-3 pb-3">
            <AsyncTaskUI.Loader forTask={matchDocumentGeneration}>
              <MarketingDocumentGeneratingPanelRow />
            </AsyncTaskUI.Loader>
            {documents.map((doc) => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                creatorName={documentCreatorNames.get(doc.id) ?? null}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
